import { useEffect, useRef, useState, useMemo, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Home from "./pages/Home/Home";
//import './App.css'
import { fetchGlobalData } from "./utils/utils";
import { useQuery } from "@tanstack/react-query";
import { Global_Plain } from "./types/api/global.ts";
import Regulatory from "./pages/Regulatory/Regulatory";
import RegulatoryDTrading from "./pages/RegulatoryDTrading/RegulatoryDTrading";
import Careers from "./pages/Careers/Careers";
import Pages from "./pages/Pages/Pages";
import HomeUkraine from "./pages/HomeUkraine/HomeUkraine";
import { useTranslation } from "react-i18next";
import CareersUkraine from "./pages/CareersUkraine/CareersUkraine";
import RegulatoryUkraine from "./pages/RegulatoryUkraine/RegulatoryUkraine";
import RegulatoryUkraineTmp from "./pages/RegulatoryUkraineTmp/RegulatoryUkraine";
import CareersDTrading from "./pages/CareersDTrading/CareersDTrading";
import InfoUkraine from "./pages/InfoUkraine/InfoUkraine";
import {
  getCountryByIP,
  getRedirectPathByCountry,
  hasLanguageCheckCookie,
  setLanguageCheckCookie,
  setLanguageCookie,
} from "./utils/languageDetection";
import PagesUkraine from "./pages/PagesUkraine/PagesUkraine";
import News from "./pages/News/News";
import NewsPage from "./pages/NewsPage/NewsPage";

// File Protection imports
import { useFileProtection } from "./hooks/useFileProtection";

const Cookie = lazy(() => import("./components/Cookie/Cookie"));
const FileAuthModal = lazy(
  () => import("./components/FileAuthModal/FileAuthModal"),
);
import NewsUkraine from "./pages/NewsUkraine/NewsUkraine.tsx";
import NewsPageUkraine from "./pages/NewsPageUkraine/NewsPageUkraine.tsx";
import ProductPage from "./pages/ProductPage/ProductPage.tsx";
import ProductPageUkraine from "./pages/ProductPageUkraine/ProductPageUkraine.tsx";
import Esg from "./pages/Esg/Esg.tsx";
import EsgTest from "./pages/EsgTest/EsgTest.tsx";
import DynamicPage from "./pages/DynamicPage/DynamicPage.tsx";
import TeamMemberPage from "./pages/TeamMemberPage/TeamMemberPage.tsx";

import { storeTrackingParams } from "./utils/tracking";
import { updateCanonicalURL } from "./utils/seo";
import { shouldShowQueryLoading } from "./utils/queryHydration";

// Helper function to determine locale from pathname (must match entry-server getLocaleFromUrl for hydration)
function getLocaleFromPathname(pathname: string): string {
  if (pathname.indexOf("/ua") === 0) {
    return "uk";
  } else if (pathname.indexOf("/en") === 0) {
    return "en";
  } else if (pathname.indexOf("/pl") === 0) {
    return "pl";
  } else if (pathname.indexOf("/ro") === 0) {
    return "ro";
  } else if (pathname.indexOf("/hr") === 0) {
    return "hr";
  } else if (pathname.indexOf("/el") === 0) {
    return "el";
  } else if (pathname.indexOf("/bg") === 0) {
    return "bg";
  } else if (pathname.indexOf("/lt") === 0) {
    return "lt";
  } else if (pathname.indexOf("/it") === 0) {
    return "it";
  } else if (pathname.indexOf("/hu") === 0) {
    return "hu";
  }
  return "en"; // "/" and other paths: match server so SSR and first client render are identical
}

function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  // Must match SSR (cookie unreadable on server) — sync after mount in useEffect below
  const [isLanguageChecked, setIsLanguageChecked] = useState(false);

  // Determine locale from pathname immediately (works on SSR)
  const currentLocale = useMemo(
    () => getLocaleFromPathname(location.pathname),
    [location.pathname],
  );

  const [locale, setLocale] = useState(currentLocale);
  const [isIPDetectionInProgress, setIsIPDetectionInProgress] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // File Protection hook
  const {
    isModalOpen,
    isLoading: fileProtectionLoading,
    error: fileProtectionError,
    handleAuthenticate,
    closeModal,
  } = useFileProtection();

  // Store tracking parameters (UTM, etc.) from URL
  useEffect(() => {
    storeTrackingParams();
  }, [location.search]);

  // Language detection and redirection on first visit
  useEffect(() => {
    const checkLocationAndRedirect = async () => {
      if (
        hasLanguageCheckCookie() ||
        isLanguageChecked ||
        isIPDetectionInProgress
      ) {
        return;
      }

      setIsIPDetectionInProgress(true);

      try {
        const countryCode = await getCountryByIP();
        console.log("Detected country:", countryCode);

        const languageCode =
          countryCode.toLowerCase() === "ua"
            ? "uk"
            : countryCode.toLowerCase() === "pl"
              ? "pl"
              : countryCode.toLowerCase() === "ro"
                ? "ro"
                : countryCode.toLowerCase() === "gr"
                  ? "el"
                  : countryCode.toLowerCase() === "bg"
                    ? "bg"
                    : countryCode.toLowerCase() === "lt"
                      ? "lt"
                      : countryCode.toLowerCase() === "it"
                        ? "it"
                        : countryCode.toLowerCase() === "hu"
                          ? "hu"
                          : "en";
        setLanguageCookie(languageCode);

        const redirectPath = getRedirectPathByCountry(countryCode);
        console.log("redirectPath:", redirectPath);

        if (redirectPath) {
          setLanguageCheckCookie();
          setIsLanguageChecked(true);
          navigate(redirectPath, { replace: true });
        } else {
          setLanguageCheckCookie();
          setIsLanguageChecked(true);
        }
      } catch (error) {
        console.error("Error during IP detection:", error);
        setLanguageCheckCookie();
        setIsLanguageChecked(true);
      } finally {
        setIsIPDetectionInProgress(false);
      }
    };

    const pathname = location.pathname;
    const isHomePath =
      pathname === "/" ||
      pathname === "/ua" ||
      pathname === "/en" ||
      pathname === "/pl" ||
      pathname === "/ro" ||
      pathname === "/hr" ||
      pathname === "/el" ||
      pathname === "/bg" ||
      pathname === "/lt" ||
      pathname === "/it" ||
      pathname === "/hu" ||
      pathname === "/ua/" ||
      pathname === "/en/" ||
      pathname === "/pl/" ||
      pathname === "/ro/" ||
      pathname === "/hr/" ||
      pathname === "/el/" ||
      pathname === "/bg/" ||
      pathname === "/lt/" ||
      pathname === "/it/" ||
      pathname === "/hu/";

    if (isHomePath) {
      checkLocationAndRedirect();
    } else {
      if (hasLanguageCheckCookie()) {
        if (!isLanguageChecked) setIsLanguageChecked(true);
      } else if (!isLanguageChecked) {
        setLanguageCheckCookie();
        setIsLanguageChecked(true);
      }
    }
  }, [location.pathname, isLanguageChecked, navigate, isIPDetectionInProgress]);

  useEffect(() => {
    // Allow /en/page/* routes to work without redirect
    if (
      location.pathname.indexOf("/en") === 0 &&
      !location.pathname.startsWith("/en/page/")
    ) {
      if (location.pathname.indexOf("/en/") === 0) {
        navigate(location.pathname.replace("/en/", "/ua/"), { replace: true });
        return;
      } else {
        navigate("/ua", { replace: true });
        return;
      }
    }

    if (location.pathname.indexOf("/uk") === 0) {
      if (location.pathname.indexOf("/uk/") === 0) {
        navigate(location.pathname.replace("/uk/", "/ua/"), { replace: true });
        return;
      } else {
        navigate("/ua", { replace: true });
        return;
      }
    }

    console.log(
      "Current path:",
      location.pathname,
      "Determined locale:",
      currentLocale,
    );

    if (currentLocale) {
      if (currentLocale === "uk") {
        i18n.changeLanguage("uk");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("uk");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "uk";
        }
        setTimeout(() => {
          //console.log("Final language after attempts:", i18n.resolvedLanguage);
        }, 100);
      } else if (currentLocale === "pl") {
        i18n.changeLanguage("pl");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("pl");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "pl";
        }
      } else if (currentLocale === "ro") {
        i18n.changeLanguage("ro");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("ro");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "ro";
        }
      } else if (currentLocale === "hr") {
        i18n.changeLanguage("hr");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("hr");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "hr";
        }
      } else if (currentLocale === "el") {
        i18n.changeLanguage("el");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("el");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "el";
        }
      } else if (currentLocale === "bg") {
        i18n.changeLanguage("bg");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("bg");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "bg";
        }
      } else if (currentLocale === "lt") {
        i18n.changeLanguage("lt");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("lt");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "lt";
        }
      } else if (currentLocale === "it") {
        i18n.changeLanguage("it");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("it");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "it";
        }
      } else if (currentLocale === "hu") {
        i18n.changeLanguage("hu");
        if (
          typeof i18n.services?.languageDetector?.cacheUserLanguage ===
          "function"
        ) {
          i18n.services.languageDetector.cacheUserLanguage("hu");
        }
        if (i18n.options && typeof i18n.options.lng !== "undefined") {
          i18n.options.lng = "hu";
        }
      }

      setLocale(currentLocale);
      if (typeof window !== "undefined") {
        localStorage.setItem("lang", currentLocale);
      }
    }
  }, [location.pathname, navigate, i18n, currentLocale]);

  // Update canonical URL on every route change (self-referencing)
  useEffect(() => {
    if (typeof document !== "undefined") {
      updateCanonicalURL();
    }
  }, [location.pathname]);

  // Update HTML lang attribute based on current locale
  useEffect(() => {
    if (locale && typeof document !== "undefined") {
      const htmlLang =
        locale === "uk"
          ? "uk"
          : locale === "pl"
            ? "pl"
            : locale === "ro"
              ? "ro"
              : locale === "hr"
                ? "hr"
                : locale === "el"
                  ? "el"
                  : locale === "bg"
                    ? "bg"
                    : locale === "lt"
                      ? "lt"
                      : locale === "it"
                        ? "it"
                        : locale === "hu"
                          ? "hu"
                          : "en";
      document.documentElement.lang = htmlLang;
    }
  }, [locale]);

  // Derive from pathname-derived locale so SSR and first client paint always match the
  // dehydrated React Query key (locale state can lag behind useEffect after navigation).
  const globalDataUrl =
    currentLocale === "uk" ||
    currentLocale === "pl" ||
    currentLocale === "ro" ||
    currentLocale === "hr" ||
    currentLocale === "el" ||
    currentLocale === "bg" ||
    currentLocale === "lt" ||
    currentLocale === "it" ||
    currentLocale === "hu"
      ? `/api/global-ukraine?customPopulate=nested&locale=${currentLocale}`
      : "/api/global?customPopulate=nested";

  const {
    data: global,
    error,
    isLoading,
    isError,
  } = useQuery<Global_Plain>({
    queryKey: [globalDataUrl],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
    enabled: !!globalDataUrl,
  });

  const lenisRef = useRef<{
    raf: (time: number) => void;
    scrollTo: (...args: unknown[]) => void;
    destroy: () => void;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let destroyed = false;
    let rafId = 0;

    const startLenis = () => {
      void import("lenis").then(({ default: Lenis }) => {
        if (destroyed) return;

        const lenis = new Lenis({
          duration: 1.2,
          easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });

        lenisRef.current = lenis;

        const raf = (time: number) => {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        };

        rafId = requestAnimationFrame(raf);
      });
    };

    if (document.readyState === "complete") {
      startLenis();
    } else {
      window.addEventListener("load", startLenis, { once: true });
    }

    return () => {
      destroyed = true;
      cancelAnimationFrame(rafId);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash || !lenisRef.current) return;

    const targetId = location.hash.substring(1);
    let attempts = 0;
    const maxAttempts = 10;
    const baseDelay = 300;

    const scrollToElement = () => {
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        lenisRef.current?.scrollTo(targetElement, {
          immediate: false,
        });
        console.log(
          `Scrolled to element #${targetId} after ${attempts + 1} attempts`,
        );
      } else if (attempts < maxAttempts) {
        attempts++;
        const delay = baseDelay * Math.pow(1.5, attempts);
        console.log(
          `Element #${targetId} not found, retrying in ${delay}ms (attempt ${attempts}/${maxAttempts})`,
        );
        setTimeout(scrollToElement, delay);
      } else {
        console.warn(
          `Element #${targetId} not found after ${maxAttempts} attempts`,
        );
      }
    };

    setTimeout(scrollToElement, 100);
  }, [location.hash]);

  useEffect(() => {
    const handleMenuScroll = (e: Event) => {
      const boxNav = document.querySelector(".box-nav");
      if (boxNav && boxNav.scrollHeight > boxNav.clientHeight && isMenuOpen) {
        e.preventDefault();
        e.stopPropagation();
        if (e instanceof WheelEvent) {
          boxNav.scrollTop += e.deltaY;
        } else if (e instanceof TouchEvent) {
          const touch = e.touches[0];
          const previousTouch = e.touches[1] || { clientY: touch.clientY };
          boxNav.scrollTop -= (touch.clientY - previousTouch.clientY) * 2;
        }
      }
    };

    if (isMenuOpen) {
      const boxNav = document.querySelector(".box-nav");
      if (boxNav) {
        boxNav.addEventListener("wheel", handleMenuScroll as EventListener, {
          passive: false,
        });
        boxNav.addEventListener(
          "touchmove",
          handleMenuScroll as EventListener,
          { passive: false },
        );
      }
    }

    return () => {
      const boxNav = document.querySelector(".box-nav");
      if (boxNav) {
        boxNav.removeEventListener("wheel", handleMenuScroll as EventListener);
        boxNav.removeEventListener(
          "touchmove",
          handleMenuScroll as EventListener,
        );
      }
    };
  }, [isMenuOpen]);

  if (shouldShowQueryLoading(isLoading, global))
    return <div className="loading-block"></div>;
  if (isError) return <div>Error: {error.message}</div>;

  // Same tree on server and client when header/global is missing (avoids hydration #418).
  // Previously client rendered an error <div> while SSR rendered loading-block.
  if (!global || !global.data || !global.data.header) {
    return <div className="loading-block"></div>;
  }

  return (
    <>
      <div>
        <Header
          data={global.data.header}
          locale={locale}
          onMenuStateChange={setIsMenuOpen}
        />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ua" element={<HomeUkraine locale={locale} />} />
          <Route path="/en" element={<HomeUkraine locale={locale} />} />
          <Route path="/pl" element={<HomeUkraine locale={locale} />} />
          <Route path="/ro" element={<HomeUkraine locale={locale} />} />
          <Route path="/hr" element={<HomeUkraine locale={locale} />} />
          <Route path="/el" element={<HomeUkraine locale={locale} />} />
          <Route path="/bg" element={<HomeUkraine locale={locale} />} />
          <Route path="/lt" element={<HomeUkraine locale={locale} />} />
          <Route path="/it" element={<HomeUkraine locale={locale} />} />
          <Route path="/hu" element={<HomeUkraine locale={locale} />} />
          <Route path="/news" element={<News locale="" />} />
          <Route path="/news/:slug" element={<NewsPage locale="" />} />
          <Route path="/products/:slug" element={<ProductPage />} />
          <Route path="/esg" element={<Esg locale={""} />} />
          <Route path="/esg_test" element={<EsgTest locale={""} />} />
          <Route path="/team/:slug" element={<TeamMemberPage locale="" />} />
          <Route
            path="/ua/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/en/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/pl/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/ro/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/hr/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/el/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/bg/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/lt/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/it/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route
            path="/hu/team/:slug"
            element={<TeamMemberPage locale={locale} />}
          />
          <Route path="/page/:slug" element={<DynamicPage locale={locale} />} />
          <Route
            path="/ua/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/en/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/pl/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/ro/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/hr/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/el/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/bg/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/lt/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/it/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route
            path="/hu/page/:slug"
            element={<DynamicPage locale={locale} />}
          />
          <Route path="/ua/news" element={<NewsUkraine locale={locale} />} />
          <Route
            path="/ua/news/:slug"
            element={<NewsPageUkraine locale={locale} />}
          />
          <Route path="/en/news" element={<NewsUkraine locale={locale} />} />
          <Route
            path="/en/news/:slug"
            element={<NewsPageUkraine locale={locale} />}
          />
          <Route path="/pl/news" element={<News locale={locale} />} />
          <Route path="/pl/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/ro/news" element={<News locale={locale} />} />
          <Route path="/ro/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/hr/news" element={<News locale={locale} />} />
          <Route path="/hr/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/el/news" element={<News locale={locale} />} />
          <Route path="/el/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/bg/news" element={<News locale={locale} />} />
          <Route path="/bg/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/lt/news" element={<News locale={locale} />} />
          <Route path="/lt/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/it/news" element={<News locale={locale} />} />
          <Route path="/hu/news" element={<News locale={locale} />} />
          <Route path="/it/news/:slug" element={<NewsPage locale={locale} />} />
          <Route path="/hu/news/:slug" element={<NewsPage locale={locale} />} />
          <Route
            path="/ua/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/en/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/pl/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/ro/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/hr/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/el/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/bg/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/lt/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/it/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route
            path="/hu/products/:slug"
            element={<ProductPageUkraine locale={locale} />}
          />
          <Route path="/regulatory" element={<Regulatory />} />
          <Route
            path="/ua/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/en/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/pl/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/ro/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/hr/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/el/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/bg/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/lt/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/it/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/hu/regulatory-dtrading"
            element={<RegulatoryDTrading locale={locale} />}
          />
          <Route
            path="/ua/regulatory_tmp"
            element={<RegulatoryUkraineTmp locale={locale} />}
          />
          <Route
            path="/ua/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/en/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/pl/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/ro/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/hr/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/el/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/bg/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/lt/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/it/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route
            path="/hu/regulatory"
            element={<RegulatoryUkraine locale={locale} />}
          />
          <Route path="/ua/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/en/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/pl/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/ro/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/hr/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/el/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/bg/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/lt/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/it/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/hu/info" element={<InfoUkraine locale={locale} />} />
          <Route path="/careers" element={<Careers />} />
          <Route
            path="/ua/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/en/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/pl/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/ro/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/hr/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/el/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/bg/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/lt/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/it/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/hu/careers-dtrading"
            element={<CareersDTrading locale={locale} />}
          />
          <Route
            path="/ua/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/en/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/pl/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/ro/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/hr/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/el/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/bg/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/lt/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/it/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route
            path="/hu/careers"
            element={<CareersUkraine locale={locale} />}
          />
          <Route path="/ua/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/en/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/pl/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/ro/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/hr/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/el/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/bg/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/lt/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/it/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/hu/:slug" element={<PagesUkraine locale={locale} />} />
          <Route path="/:slug" element={<Pages />} />
          <Route path="/ua/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/en/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/pl/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/ro/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/hr/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/el/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/bg/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/lt/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/it/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/hu/*" element={<PagesUkraine locale={locale} />} />
          <Route path="/*" element={<Pages />} />
        </Routes>
        <Footer data={global.data.footer} locale={locale} />
        <Suspense fallback={null}>
          <Cookie />
        </Suspense>

        {/* File Authentication Modal */}
        <Suspense fallback={null}>
          <FileAuthModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onAuthenticate={handleAuthenticate}
            isLoading={fileProtectionLoading}
            error={fileProtectionError}
          />
        </Suspense>
      </div>
    </>
  );
}

export default App;
